import React from 'react';
import { Link } from 'react-router-dom';
import { metaStyle as meta } from '../utils/shopHealth';
import { useShopOffer } from '../hooks/useShopOffer';
import ShopTable from '../components/ShopTable';

// The shops this publisher's networks say they are approved for and does not yet
// sell from.
//
// It has its own route because it is a DISTINCT INTENT with its own frequency:
// adding supply is a burst of activity once, while acting on supply that stopped
// is weekly. On the hub it sat below every supplied shop — 14.7 phone screens
// down at the live account's size, and 32 at a hundred shops — so the rarer job
// was the one that got further away every time the commoner one succeeded.
//
// IT ASKS FOR ONE FIGURE, BECAUSE ONLY ONE OF THE HUB'S THREE CAN EVER HAVE A
// VALUE HERE. `addable` is `!supplied`, and nothing is `!supplied` while having a
// feed of its own — so `statusWord` is the constant "Not added" and the outcome
// row is null, by construction rather than by luck. Measured on the live account:
// 1 distinct State on 11 of 11 rows, an empty Shown · clicked on 11 of 11, 40% of
// the table's width at both viewports, and 166px of sideways scroll at 390px
// whose whole payload was the blank column.
//
// Passing no `outcomeFor` is the point rather than a shortcut: this page now
// holds no dependency at all on a read it cannot render. `useShopOffer` still
// FETCHES it, for the two surfaces that can.
//
// AND THE ONE FIGURE IT KEEPS IS `catalogue`, NOT `products`. Both were the same
// column called `Products`, and they are different quantities: the hub's is what
// this publisher HAS, the picker's is how big the shop's own catalogue is, which
// `shop-list.ts` marks "never presented as what the publisher will receive". A
// publisher comparing `librariadelfin 236,696` here with `answear 56,738` on the
// hub was comparing two unlike numbers under one word — and this is the only
// figure the picker offers them to choose on. Live range: 2 to 422,024.

const AddShopsPage: React.FC = () => {
  const { shops, consent, failed } = useShopOffer();

  if (failed) {
    return (
      <section className="stats-section" data-testid="add-shops-error">
        <p role="alert" style={{ color: 'var(--danger)' }}>
          We could not load your shops just now. Reload the page to try again — nothing has changed.
        </p>
        <p><Link to="/feeds">Back to your shops</Link></p>
      </section>
    );
  }

  if (!shops) {
    return <section className="stats-section"><p style={meta}>Loading…</p></section>;
  }

  const addable = shops.filter(s => !s.supplied);

  return (
    <>
      <p style={{ ...meta, marginBottom: '0.75rem' }}>
        <Link to="/feeds" data-testid="back-to-feeds">← Your shops</Link>
      </p>

      <section className="stats-section" data-testid="add-shops">
        <h2>Shops you could add{addable.length > 0 && ` (${addable.length})`}</h2>

        {addable.length === 0 ? (
          <p style={meta} data-testid="picker-empty">
            Connect an affiliate network and the shops you are approved for will appear here,
            ready to add without uploading anything.{' '}
            <Link to="/networks">See your networks</Link>.
          </p>
        ) : (
          <ShopTable
            shops={addable}
            columns={['catalogue']}
            testId="shop-table-addable"
            caption={
              <>
                <p style={meta} data-testid="picker-order">
                  Ordered by how these shops perform for affiliates across the network — approval
                  rate, earnings per click and whether they are in budget. Open one to add it.
                </p>
                {/* SERVED, NEVER WRITTEN HERE. Adding a shop writes a product
                    export into the publisher's own affiliate-network account —
                    the only thing in this portal whose consequence lands outside
                    our systems — and a copy of that sentence in the client would
                    go on describing behaviour the server had changed. */}
                <p style={meta} data-testid="picker-consent">{consent}</p>
              </>
            }
          />
        )}

        <p style={{ ...meta, marginTop: '0.75rem' }}>
          Cannot find a shop here? Your network only lists programmes you are approved for. You can
          also <Link to="/feeds/upload">upload a feed file</Link> or{' '}
          <Link to="/feeds/connect">connect one by URL</Link>.
        </p>
      </section>
    </>
  );
};

export default AddShopsPage;
